import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CMS_API_BASE_URL, CMS_DEV_TOKEN } from './cms-api.tokens';
import { CmsTokenBridgeService } from '../cms-auth/cms-token-bridge.service';
import { CmsDevAuthService } from '../cms-auth/cms-dev-auth.service';

export interface CmsItem {
  id?: number;
  title: string;
  description: string;
  image: string;
  labels: string[];
  imageBase64?: string;
  imageContentType?: string;
}

@Injectable({ providedIn: 'root' })
export class CmsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(CMS_API_BASE_URL);
  private readonly devToken = inject(CMS_DEV_TOKEN);
  private readonly bridge = inject(CmsTokenBridgeService);
  private readonly devAuth = inject(CmsDevAuthService);

  // ── Products
  getProducts(): Observable<CmsItem[]> {
    return this.http.get<CmsItem[]>(`${this.baseUrl}/api/products`, {
      headers: this.headers()
    });
  }

  publishProducts(items: CmsItem[]): Observable<CmsItem[]> {
    return this.http.put<CmsItem[]>(
      `${this.baseUrl}/api/products/publish`,
      this.prepareForPublish(items),
      { headers: this.headers() }
    );
  }

  deleteProduct(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/products/${id}`, {
      headers: this.headers()
    });
  }

  // ── Services
  getServices(): Observable<CmsItem[]> {
    return this.http.get<CmsItem[]>(`${this.baseUrl}/api/services`, {
      headers: this.headers()
    });
  }

  publishServices(items: CmsItem[]): Observable<CmsItem[]> {
    return this.http.put<CmsItem[]>(
      `${this.baseUrl}/api/services/publish`,
      this.prepareForPublish(items),
      { headers: this.headers() }
    );
  }

  deleteService(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/services/${id}`, {
      headers: this.headers()
    });
  }

  // ── News
  getNews(): Observable<CmsItem[]> {
    return this.http.get<CmsItem[]>(`${this.baseUrl}/api/news`, {
      headers: this.headers()
    });
  }

  publishNews(items: CmsItem[]): Observable<CmsItem[]> {
    return this.http.put<CmsItem[]>(
      `${this.baseUrl}/api/news/publish`,
      this.prepareForPublish(items),
      { headers: this.headers() }
    );
  }

  deleteNews(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/news/${id}`, {
      headers: this.headers()
    });
  }

  private prepareForPublish(items: CmsItem[]): CmsItem[] {
    const s3HostMarker = '.amazonaws.com/';

    return items.map((item) => {
      if (item.image && item.image.startsWith('data:')) {
        const contentType =
          item.image.match(/^data:([^;]+);/)?.[1] ?? 'image/png';
        const base64 = item.image.split(',')[1] ?? '';
        return {
          ...item,
          image: '',
          imageBase64: base64,
          imageContentType: contentType
        };
      }
      const s3Index = item.image ? item.image.indexOf(s3HostMarker) : -1;
      if (s3Index >= 0) {
        // Extract just the object key after the bucket domain
        return {
          ...item,
          image: item.image!.substring(s3Index + s3HostMarker.length)
        };
      }
      return item;
    });
  }

  private headers(): HttpHeaders {
    const token =
      this.devToken ?? this.devAuth.getToken() ?? this.bridge.getToken();
    if (!token) return new HttpHeaders();
    return new HttpHeaders({ Authorization: `Bearer ${token}` });
  }
}
